import { useMutation } from '@tanstack/react-query';
import { toast } from '@evaneos/b2b-ui';

import { useIntl } from 'react-intl';

import { DossierId } from '../dossier/types';

import {
    retryTranscriptApi,
    retrySummaryApi,
    createPhoneCallApi,
    generateSummaryMailApi,
} from './api';
import { PhoneCallSummaryMail, PhoningMarkets } from './types';

export const useCreatePhoneCall = () => {
    return useMutation({
        mutationFn: ({ dossierId, language }: { dossierId: DossierId; language: PhoningMarkets }) =>
            createPhoneCallApi(dossierId, language),
    });
};

export const useRetryTranscript = (onSuccess?: () => void) => {
    return useMutation({
        mutationFn: ({
            dossierId,
            dossierPhoneCallId,
        }: {
            dossierId: DossierId;
            dossierPhoneCallId: string;
        }) => retryTranscriptApi(dossierId, dossierPhoneCallId),
        onSuccess,
    });
};

export const useRetrySummary = (onSuccess?: () => void) => {
    return useMutation({
        mutationFn: ({
            dossierId,
            dossierPhoneCallId,
        }: {
            dossierId: DossierId;
            dossierPhoneCallId: string;
        }) => retrySummaryApi(dossierId, dossierPhoneCallId),
        onSuccess,
    });
};

export const generateSummaryMailMutationKey = (dossierId: DossierId) =>
    ['phoneCall', 'generateSummaryMail', dossierId] as const;

export const useGenerateSummaryMail = (
    dossierId: DossierId,
    options?: {
        onSuccess?: (data: PhoneCallSummaryMail) => void;
        onError?: (error: unknown) => void;
    },
) => {
    const { formatMessage } = useIntl();
    return useMutation({
        mutationKey: generateSummaryMailMutationKey(dossierId),
        mutationFn: ({ dossierPhoneCallId }: { dossierPhoneCallId: string }) =>
            generateSummaryMailApi(dossierId, dossierPhoneCallId),
        onSuccess: options?.onSuccess,
        onError: (error: unknown) => {
            options?.onError?.(error);

            toast.error(
                formatMessage({
                    id: 'dossier.discussion.phone_call.recap_email_block.error_generating_mail',
                }),
            );
        },
    });
};
